import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { UpperCasePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { ComboBoxComponent, ItemTemplateDirective } from '@progress/kendo-angular-dropdowns';
import { FormFieldComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { switchMap, timer } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import {
  CapitalizePipe,
  Client,
  ClientEmployee,
  ClientEmployeeService,
  ClientService,
  DropdownLoaderDirective,
} from 'shared';
import { logout } from '@crowndesk-core/session/logout';
import { ImpersonationService } from './impersonation.service';

/** Lets a Royal Crown employee pick a client and one of its employees to log in as. */
@Component({
  selector: 'rc-login-as',
  imports: [
    TranslateModule,
    CapitalizePipe,
    UpperCasePipe,
    ButtonComponent,
    ComboBoxComponent,
    ItemTemplateDirective,
    FormFieldComponent,
    LabelComponent,
    DropdownLoaderDirective,
  ],
  templateUrl: './login-as.component.html',
  styleUrl: './login-as.component.scss',
})
export class LoginAsComponent {
  #clientService = inject(ClientService);
  #clientEmployeeService = inject(ClientEmployeeService);
  #impersonationService = inject(ImpersonationService);
  #authService = inject(AuthService);

  clientSearch = signal('');
  clients = rxResource({
    params: () => this.clientSearch().trim(),
    // the delay debounces typing: a new search cancels the pending one
    stream: ({ params: search }) =>
      timer(search ? 300 : 0).pipe(switchMap(() => this.#clientService.findClients(clientQuery(search)))),
    defaultValue: [],
  });
  selectedClient = signal<Client | null>(null);

  employees = rxResource({
    params: () => this.selectedClient()?.['@id'],
    // All of them: the search is local, so employees beyond the api's first page could never be picked
    stream: ({ params: clientIri }) =>
      this.#clientEmployeeService.getActiveClientEmployeesIri(clientIri, 'pagination=false'),
    defaultValue: [],
  });
  /** Both reset whenever another client is picked. */
  employeeSearch = linkedSignal({ source: this.selectedClient, computation: () => '' });
  selectedEmployee = linkedSignal<Client | null, ClientEmployee | null>({
    source: this.selectedClient,
    computation: () => null,
  });
  filteredEmployees = computed(() => {
    const search = this.employeeSearch().trim().toLowerCase();
    return this.employees
      .value()
      .filter((employee) => !search || employeeSearchText(employee).includes(search));
  });

  loginAs(): void {
    const employee = this.selectedEmployee();
    if (!employee?.id) return;
    this.#impersonationService.start(employee.id);
  }

  logout(): void {
    logout(this.#authService);
  }
}

/** Searches on customer number when only digits are typed, otherwise on name. */
function clientQuery(search: string): string {
  if (!search) return 'isActive=true';
  const field = /^\d+$/.test(search) ? 'customerNumber' : 'name';
  return `isActive=true&${field}=${encodeURIComponent(search)}`;
}

function employeeSearchText(employee: ClientEmployee): string {
  return `${employee.fullName ?? `${employee.firstName} ${employee.lastName}`} ${employee.primaryEmailAddress ?? ''}`.toLowerCase();
}
